/**
 * Pagination Component
 *
 * Renders numbered page buttons with prev/next arrows.
 * Matches the release-page pagination style.
 */
import { __ } from '@wordpress/i18n';

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    if ( totalPages <= 1 ) {
        return null;
    }

    /**
     * Build a compact page-number list with ellipses.
     *
     * Always shows first, last, current, and one neighbor on each side.
     */
    const getPageNumbers = () => {
        const pages = [];
        const delta = 1; // neighbors around current page

        for ( let i = 1; i <= totalPages; i++ ) {
            if (
                i === 1 ||
                i === totalPages ||
                ( i >= currentPage - delta && i <= currentPage + delta )
            ) {
                pages.push( i );
            } else if ( pages[ pages.length - 1 ] !== '...' ) {
                pages.push( '...' );
            }
        }
        return pages;
    };

    return (
        <nav className="cha-feedback-pagination" aria-label={ __( 'Feedback pagination', 'changeloger' ) }>
            {/* Prev */}
            <button
                type="button"
                className="cha-page-btn cha-page-prev"
                disabled={ currentPage <= 1 }
                onClick={ () => onPageChange( currentPage - 1 ) }
                aria-label={ __( 'Previous page', 'changeloger' ) }
            >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
            </button>

            {/* Page numbers */}
            { getPageNumbers().map( ( page, idx ) =>
                page === '...' ? (
                    <span key={ `ellipsis-${ idx }` } className="cha-page-ellipsis">…</span>
                ) : (
                    <button
                        key={ page }
                        type="button"
                        className={ `cha-page-btn${ page === currentPage ? ' active' : '' }` }
                        onClick={ () => onPageChange( page ) }
                        aria-current={ page === currentPage ? 'page' : undefined }
                    >
                        { page }
                    </button>
                )
            ) }

            {/* Next */}
            <button
                type="button"
                className="cha-page-btn cha-page-next"
                disabled={ currentPage >= totalPages }
                onClick={ () => onPageChange( currentPage + 1 ) }
                aria-label={ __( 'Next page', 'changeloger' ) }
            >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
            </button>
        </nav>
    );
};

export default Pagination;
